import { useTeams, useUsers } from '@/api/data';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
/**
 * Ansatte (#270, Employees V1.3): who needs attention and what to do next. Four task indicators
 * (Arbeidstid, Ansattdata, Ikke invitert, Venter på innlogging) count unique people in the scope the
 * toolbar sets; choosing one lists exactly those people with the action that fixes it. The scope and
 * the task live in the address, so Back, a refresh and a shared link keep them, and opening a person
 * keeps the list as a queue (ansatt/tasks.ts) that the profile walks and returns to.
 */
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, Empty, LoadError, Skeleton } from '@/ui';
import { avTone, fmtDs, fullName, initials, num } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { PageHead } from '@/ui/page';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import './ansatt/ansatte.css';
import { likelyDomain } from './ansatt/emailTypo';
import { AppSheet, InviteReview, invitation, inviteText } from './ansatt/invite';
import { endNotice, ruleWeek, stillingsprosent } from './ansatt/roster';
import {
  type Row,
  type Scope,
  type Task,
  type TaskKey,
  accessOf,
  countTasks,
  fieldFor,
  hoursOf,
  inScope,
  inTask,
  loadQueue,
  readScope,
  saveQueue,
  setupGaps,
  teamsHere,
  writeScope,
} from './ansatt/tasks';

/** The list's heading per task, the prototype's words. */
const TASK_TITLE: Record<Task, string> = {
  all: 'Alle ansatte',
  hours: 'Avtalt arbeidstid',
  setup: 'Ansettelsesopplysninger',
  number: 'Mangler ansattnummer',
  uninvited: 'Ikke invitert',
  pending: 'Venter på første innlogging',
};
const GAP_TEXT: Record<string, string> = {
  employee_id: 'Ansattnummer',
  employment_start: 'Startdato',
  birth_date: 'Fødselsdato',
};
/** «Ansattnummer og startdato mangler», «Startdato mangler»: the gaps as one phrase. */
const gapPhrase = (gaps: string[]) => {
  const words = gaps.map((g) => T(GAP_TEXT[g] ?? g));
  const list = words.length > 1 ? `${words.slice(0, -1).join(', ')} ${T('og')} ${words.at(-1)}` : words[0];
  return T('{list} mangler', {
    list: list?.toLocaleLowerCase('nb').replace(/^./, (c) => c.toUpperCase()) ?? '',
  });
};

const ansatte = (n: number) => T('{n, plural, one {ansatt} other {ansatte}}', { n });

export default function Ansatte() {
  const can = useCan();
  const nav = useNavigate();
  const loc = useLocation();
  const navType = useNavigationType();
  const [sp, setSp] = useSearchParams();
  const { tenant } = useTenant();
  const meId = useAuth((s) => s.user?.id);
  const usersQ = useUsers();
  const teamsQ = useTeams();
  const mayEdit = can('ansatte.endre');
  // the invitation facts reach the administrator's read only (#120); anyone else sees them as unknown
  const accessKnown = tenant?.me?.is_admin === true;
  const scope = readScope(sp);
  const [q, setQ] = useState(scope.q);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // the phone placeholder is the short one, so 390 and 320 never cut it mid-word
  const phone = typeof matchMedia === 'function' && matchMedia('(max-width: 800px)').matches;
  const narrow = typeof matchMedia === 'function' && matchMedia('(max-width: 370px)').matches;
  const [review, setReview] = useState<number[] | null>(null);
  const [sheet, setSheet] = useState(false);
  const users = (usersQ.data ?? []) as Row[];
  const set = (patch: Partial<Scope & { task: Task }>, replace = false) =>
    setSp(writeScope({ ...scope, q, ...patch }), { replace });
  // the search writes the address as it is typed, replacing, so Back leaves the page instead of undoing letters
  useEffect(() => {
    if (q !== scope.q) setSp(writeScope({ ...scope, q }), { replace: true });
  }, [q, scope, setSp]);

  // teams: the company's list where the reader may read it, else the teams the people carry
  const teams = new Map<number, string>();
  for (const t of teamsQ.data ?? []) if (t.name) teams.set(t.id, t.name);
  for (const u of users) for (const t of teamsHere(u, tenant?.id)) if (t.name) teams.set(t.id, t.name);
  const teamList = [...teams.entries()].sort((a, b) => a[1].localeCompare(b[1], 'nb'));

  const base = inScope(users, scope, tenant?.id);
  const counts = countTasks(base, accessKnown);
  const rows = base.filter((u) => inTask(u, scope.task, accessKnown));
  const companyWeek = ruleWeek(tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : null);
  const tenantWeek = tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : 37.5;

  // Back from a profile, or its «Tilbake»: the keyboard and the page return to the person opened
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !usersQ.isSuccess) return;
    restored.current = true;
    const queue = loadQueue(tenant?.id, meId);
    const st = loc.state as { restore?: number } | null;
    // the queue keeps its address in writeScope's order; compare in that order, not as typed
    const norm = writeScope(readScope(new URLSearchParams(loc.search))).toString();
    const here = `${loc.pathname}${norm ? `?${norm}` : ''}`;
    const id = st?.restore ?? (navType === 'POP' && queue?.origin === here ? queue.focus : null);
    if (id == null) return;
    requestAnimationFrame(() => {
      if (queue?.origin === here) window.scrollTo(0, queue.scroll);
      (document.getElementById(`ans-${id}`) ?? document.getElementById('ans-list-title'))?.focus({
        preventScroll: queue?.origin === here,
      });
    });
  }, [usersQ.isSuccess, loc, navType, tenant?.id, meId]);

  /** Opening a person keeps this list as the queue the profile walks and returns to. */
  const open = (u: Row, field?: string | null) => {
    if (tenant && meId)
      saveQueue({
        tenant: tenant.id,
        user: meId,
        origin: `${loc.pathname}?${writeScope({ ...scope, q })}`.replace(/\?$/, ''),
        ids: rows.map((r) => r.id),
        task: scope.task,
        focus: u.id,
        scroll: window.scrollY,
      });
    nav(`/ansatte/${u.id}${field ? `?rediger=${field}` : ''}`);
  };

  const failed = usersQ.isError && !usersQ.data;
  const stale = usersQ.isError && !!usersQ.data;
  const companyEmpty = usersQ.isSuccess && users.length === 0;
  const meta = !usersQ.isSuccess
    ? T('Kontakt, arbeidsforhold og tilgang')
    : `${base.length} ${
        scope.who === 'aktive'
          ? T('{n, plural, one {aktiv} other {aktive}}', { n: base.length })
          : scope.who === 'inaktive'
            ? T('{n, plural, one {inaktiv} other {inaktive}}', { n: base.length })
            : T('{n, plural, one {ansatt i utvalget} other {ansatte i utvalget}}', { n: base.length })
      }, ${scope.team == null ? T('alle team') : scope.team === 'none' ? T('uten team') : (teams.get(scope.team) ?? T('team'))}`;
  const scopeLabel = [
    scope.team == null
      ? T('Alle team')
      : scope.team === 'none'
        ? T('Uten team')
        : (teams.get(scope.team) ?? ''),
    scope.who === 'aktive'
      ? T('Aktive ansatte')
      : scope.who === 'inaktive'
        ? T('Inaktive ansatte')
        : T('Alle arbeidsforhold'),
    scope.q.trim() ? T('Søk: «{q}»', { q: scope.q.trim() }) : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="ans-page">
      <PageHead
        icon="users"
        title={T('Ansatte')}
        sub={meta}
        actions={
          mayEdit && (
            <>
              <Button onClick={() => nav('/ansatte/importer')} data-testid="ans-import">
                <Icon name="upload" />
                {T('Hent ansatte')}
              </Button>
              <Button
                tone="pri"
                onClick={() =>
                  nav(`/ansatte/ny${typeof scope.team === 'number' ? `?team=${scope.team}` : ''}`, {
                    state: { from: `${loc.pathname}${loc.search}` },
                  })
                }
              >
                <Icon name="plus" />
                {T('Ny ansatt')}
              </Button>
            </>
          )
        }
      />
      {failed ? (
        <LoadError what={T('de ansatte')} onRetry={() => usersQ.refetch()} />
      ) : !usersQ.isSuccess && !stale ? (
        <div className="ans-loading">
          <output className="ans-mute">{T('Henter ansatte og opplysninger …')}</output>
          <Skeleton rows={4} />
        </div>
      ) : companyEmpty ? (
        <Empty
          tone="setup"
          title={T('Legg til den første ansatte')}
          action={
            mayEdit ? (
              <>
                <Button tone="pri" onClick={() => nav('/ansatte/importer')}>
                  {T('Hent ansatte')}
                </Button>
                <Button onClick={() => nav('/ansatte/ny')}>{T('Ny ansatt')}</Button>
              </>
            ) : undefined
          }
        >
          {mayEdit
            ? T(
                'Samle kontakt og arbeidsforhold her. Du velger separat om den ansatte skal inviteres til Timefly.',
              )
            : T('Be en administrator legge til ansatte.')}
        </Empty>
      ) : (
        <>
          {stale && (
            <div className="mnote warn ans-stale">
              <Icon name="warn" />
              <div className="mnt">
                <b>{T('Viser sist kjente opplysninger')}</b>
                <div>
                  {T('Hentet kl. {t}. Oppdater oversikten før du gjør endringer.', {
                    t: new Date(usersQ.dataUpdatedAt).toLocaleTimeString('nb-NO', {
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  })}
                </div>
              </div>
              <div className="mna">
                <Button onClick={() => usersQ.refetch()}>{T('Oppdater')}</Button>
              </div>
            </div>
          )}
          {!mayEdit && (
            <p className="ans-readonly" data-testid="ansatte-lesetilgang">
              <Icon name="info" />
              {T('Du ser kontakt og team. En administrator endrer ansattopplysninger og tilgang.')}
            </p>
          )}
          {mayEdit && (
            <Indicators
              counts={counts}
              // the employee-number list narrows Ansattdata, so that indicator stays the chosen one
              selected={scope.task === 'number' ? 'setup' : scope.task}
              onPick={(k) => set({ task: k })}
              onReview={
                accessKnown &&
                (scope.task === 'uninvited' || scope.task === 'pending') &&
                (counts[scope.task] ?? 0) > 0
                  ? () => setReview(rows.map((r) => r.id))
                  : null
              }
            />
          )}
          <div className="ans-toolwrap">
            <div className={`ans-tools${filtersOpen ? ' open' : ''}`}>
              <label className="ans-search">
                <span>{T('Søk etter ansatt')}</span>
                <span className="ans-sw">
                  <Icon name="search" />
                  <input
                    className="inp"
                    type="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={
                      narrow
                        ? T('Navn eller nr.')
                        : phone
                          ? T('Navn, nummer, e-post')
                          : T('Navn, nummer, e-post eller team')
                    }
                    autoComplete="off"
                    data-testid="ansatte-sok"
                  />
                </span>
              </label>
              <button
                type="button"
                className="btn ans-ftog"
                aria-expanded={filtersOpen}
                aria-controls="ans-filters"
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <Icon name="sliders" />
                {T('Filtre')}
              </button>
              <div id="ans-filters" className="ans-filters">
                {teamList.length > 0 && (
                  <label className="mf">
                    {T('Team')}
                    <select
                      className="inp"
                      data-testid="ansatte-team"
                      value={scope.team == null ? '' : String(scope.team)}
                      onChange={(e) =>
                        set({
                          team:
                            e.target.value === ''
                              ? null
                              : e.target.value === 'none'
                                ? 'none'
                                : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">{T('Alle team')}</option>
                      {teamList.map(([id, name]) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                      <option value="none">{T('Uten team')}</option>
                    </select>
                  </label>
                )}
                <label className="mf">
                  {T('Arbeidsforhold')}
                  <select
                    className="inp"
                    data-testid="ansatte-vis"
                    value={scope.who}
                    onChange={(e) => set({ who: e.target.value as Scope['who'] })}
                  >
                    <option value="aktive">{T('Aktive')}</option>
                    <option value="inaktive">{T('Inaktive')}</option>
                    <option value="alle">{T('Alle')}</option>
                  </select>
                </label>
                {mayEdit && (
                  <label className="mf">
                    {T('Oppfølging')}
                    <select
                      className="inp"
                      data-testid="ansatte-oppgave"
                      value={scope.task}
                      onChange={(e) => set({ task: e.target.value as Task })}
                    >
                      {(
                        [
                          'all',
                          'hours',
                          'setup',
                          ...(scope.task === 'number' ? ['number'] : []),
                          ...(accessKnown ? ['uninvited', 'pending'] : []),
                        ] as Task[]
                      ).map((k) => (
                        <option key={k} value={k}>
                          {T(TASK_TITLE[k])}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>
          </div>
          <div className="ans-head">
            <div>
              <h2 id="ans-list-title" tabIndex={-1} data-testid="ansatte-n">
                {T(TASK_TITLE[scope.task])} <span className="ans-n">({rows.length})</span>
              </h2>
              <p className="ans-scope">{scopeLabel}</p>
            </div>
            {scope.task !== 'all' && (
              <button type="button" className="mlink" onClick={() => set({ task: 'all' })}>
                {T('Vis alle')}
              </button>
            )}
          </div>
          <div id="ans-results">
            {rows.length ? (
              <>
                <table className="ans-list" data-testid="ansatte">
                  <caption className="sr">
                    {T('{t} i det valgte utvalget', { t: T(TASK_TITLE[scope.task]) })}
                  </caption>
                  <thead>
                    <tr>
                      <th>{T('Ansatt')}</th>
                      {mayEdit && <th>{T('Avtalt per uke')}</th>}
                      {mayEdit && <th>{T('Ansettelsesdata')}</th>}
                      {!mayEdit && <th>{T('Kontakt')}</th>}
                      <th>{T('Tilgang til Timefly')}</th>
                      <th>
                        <span className="sr">{T('Handling')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((u) => (
                      <PersonRow
                        key={u.id}
                        u={u}
                        task={scope.task}
                        mayEdit={mayEdit}
                        accessKnown={accessKnown}
                        tenantId={tenant?.id}
                        companyWeek={companyWeek}
                        tenantWeek={tenantWeek}
                        onOpen={open}
                        onReview={(id) => setReview([id])}
                      />
                    ))}
                  </tbody>
                </table>
                <div className="ans-foot">
                  <span>
                    {T('{n} av {m}', { n: rows.length, m: base.length })} {ansatte(base.length)}{' '}
                    {T('i utvalget')}
                    {scope.task !== 'all' && ` ${T('har denne oppgaven')}`}
                  </span>
                  <span className="sp" />
                  {accessKnown && (scope.task === 'uninvited' || scope.task === 'pending') && (
                    <button
                      type="button"
                      className="mlink"
                      onClick={() => setSheet(true)}
                      data-testid="ansatte-app-ark"
                    >
                      {T('Appen på papir')}
                    </button>
                  )}
                  <span>{T('Opplysninger og invitasjoner er separate oppgaver.')}</span>
                </div>
              </>
            ) : base.length && scope.task !== 'all' ? (
              <section className="ans-done" data-testid="ansatte-oppgave-ferdig">
                <h3>{T('Ingen ansatte i denne oppgaven')}</h3>
                <p>{T('Ingen i det valgte utvalget trenger denne oppfølgingen nå.')}</p>
                <Button onClick={() => set({ task: 'all' })}>{T('Vis alle ansatte')}</Button>
              </section>
            ) : (
              <section className="ans-none" data-testid="ansatte-ingen-treff">
                <Icon name="search" size={20} />
                <h3>{T('Ingen ansatte passer utvalget')}</h3>
                <p>
                  {scope.q.trim()
                    ? T('Prøv et annet søk, eller fjern søket. Team, arbeidsforhold og oppfølging beholdes.')
                    : T('Prøv et annet team eller arbeidsforhold.')}
                </p>
                {scope.q.trim() ? (
                  <Button onClick={() => setQ('')}>{T('Tøm søket')}</Button>
                ) : (
                  <Button onClick={() => set({ team: null, who: 'aktive' })}>
                    {T('Vis alle aktive ansatte')}
                  </Button>
                )}
              </section>
            )}
          </div>
        </>
      )}
      {accessKnown && (
        <>
          <InviteReview
            ids={review}
            people={users}
            meId={meId}
            onClose={() => setReview(null)}
            onRetry={(ids) => setReview(ids)}
          />
          <AppSheet open={sheet} onClose={() => setSheet(false)} />
        </>
      )}
    </div>
  );
}

/** What an indicator says when its facts are unknown to this reader. */
const unknownText = (key: TaskKey) =>
  key === 'uninvited' || key === 'pending'
    ? T('Ikke tilgjengelig. Bare administratoren ser invitasjoner.')
    : T('Ikke tilgjengelig');
/** One indicator's inside: the icon and name, the count with its unit, and what is counted. */
const indicatorBody = (label: string, icon: IconName, n: number | null, on: boolean, detail: string) => (
  <>
    <span className="ans-il msl">
      <Icon name={icon} size={16} strokeWidth={1.7} />
      <span>{label}</span>
    </span>
    <span className="ans-iv">
      <span>
        <strong>{n == null ? '–' : n}</strong>
        {n != null && <span className="ans-iu">{ansatte(n)}</span>}
      </span>
      {on && (
        <span className="ans-ick">
          <Icon name="check" size={18} strokeWidth={1.9} />
          <span className="sr">{T('Valgt')}</span>
        </span>
      )}
    </span>
    <span className="ans-id msd">{detail}</span>
  </>
);

/**
 * The four indicators: neutral summaries (#154 operational23-2, page.css .msum), an outline icon and the
 * task's name, the count with its unit, and what is counted. Data to correct is a small amber icon; invitations
 * and waiting stay neutral. The chosen one is the lavender selection, pressed and ticked, never told by
 * colour alone. Unknown is a dash and «Ikke tilgjengelig», never 0.
 */
export function Indicators({
  counts,
  selected,
  onPick,
  onReview,
}: {
  counts: Record<TaskKey, number | null>;
  selected: Exclude<Task, 'number'>;
  onPick: (k: Task) => void;
  onReview: (() => void) | null;
}) {
  const c = counts;
  const items: [TaskKey, string, 'warn' | 'act' | 'neu', IconName, string, string][] = [
    ['hours', T('Arbeidstid'), 'warn', 'clock', T('Mangler eller er ugyldig'), T('Avtalt tid registrert')],
    [
      'setup',
      T('Ansattdata'),
      'warn',
      'document',
      T('Nummer eller dato mangler eller er feil'),
      T('Nummer og datoer registrert'),
    ],
    [
      'uninvited',
      T('Ikke invitert'),
      'act',
      'mail',
      T('Ingen invitasjon sendt'),
      T('Ingen venter på invitasjon'),
    ],
    [
      'pending',
      T('Venter på innlogging'),
      'neu',
      'login',
      T('Invitasjon er sendt'),
      T('Ingen venter på innlogging'),
    ],
  ];
  const needs = (c.hours ?? 0) + (c.setup ?? 0) > 0;
  const lines: Record<TaskKey, string> = {
    hours: c.hours
      ? T('Avtalt tid mangler eller er ugyldig. Velg «Sett arbeidstid» i listen.')
      : T('Ingen i utvalget mangler gyldig avtalt arbeidstid.'),
    setup: c.setup
      ? T('Fullfør ansattnummer, startdato eller fødselsdato for de ansatte i listen.')
      : T('Ansattnummer, startdato og fødselsdato er registrert for alle i utvalget.'),
    uninvited: c.uninvited
      ? T('Opprettede profiler uten invitasjon. Se mottakerne før du inviterer.')
      : T('Ingen i utvalget venter på å bli invitert.'),
    pending: c.pending
      ? T('Invitasjon er sendt. Første innlogging er ikke registrert ennå.')
      : T('Ingen i utvalget venter på første innlogging.'),
  };
  const complete = selected === 'all' && !needs;
  const text =
    selected === 'all'
      ? c.hours
        ? T('Avklar avtalt arbeidstid først.')
        : c.setup
          ? T('Fullfør de manglende ansettelsesopplysningene.')
          : T('Arbeidstid og ansettelsesopplysninger er på plass.')
      : lines[selected];
  let action: ReactNode = null;
  if (selected === 'all' && needs)
    action = (
      <button type="button" className="btn pri" onClick={() => onPick(c.hours ? 'hours' : 'setup')}>
        {c.hours ? T('Avklar arbeidstid') : T('Fullfør ansattdata')} <Icon name="arrowRight" />
      </button>
    );
  else if (onReview)
    action = (
      <button type="button" className="btn pri" onClick={onReview} data-testid="ansatte-se-mottakere">
        {selected === 'pending' ? T('Se mottakere og påminn') : T('Se mottakere og inviter')}
      </button>
    );
  return (
    <section className="ans-ov" aria-label={T('Oppfølging i utvalget')} data-testid="ansatte-oppgaver">
      <div className="ans-inds">
        {items.map(([key, label, tone, icon, some, none]) => {
          const n = c[key];
          const unknown = n == null;
          const on = selected === key;
          // an actual issue is a small amber icon on the neutral surface (#154 operational23-2, OP-01)
          const issue = !unknown && n && tone === 'warn' ? ' issue' : '';
          const cls = `ans-ind msum ${unknown || !n ? 'neu' : tone}${issue}${on ? ' on' : ''}`;
          const body = indicatorBody(label, icon, n, on, unknown ? unknownText(key) : n ? some : none);
          // unknown is a fact to read, not a control that does nothing: no greyed button (owner rule)
          return unknown ? (
            <div key={key} className={cls} data-testid={`ind-${key}`}>
              {body}
            </div>
          ) : (
            <button
              key={key}
              type="button"
              className={cls}
              aria-pressed={on}
              aria-controls="ans-results"
              data-testid={`ind-${key}`}
              onClick={() => onPick(key)}
            >
              {body}
            </button>
          );
        })}
      </div>
      {/* #154 BR-11: the reminder and why one person can count twice, one bordered callout with its action */}
      <div className={`ans-ctx${complete ? ' ok' : ''}`}>
        <div className="ans-ctxm">
          <Icon name={complete ? 'check' : 'info'} />
          <div>
            <p className="ans-ctxt">{text}</p>
            <p className="ans-note">{T('Én ansatt kan trenge flere oppfølginger.')}</p>
          </div>
        </div>
        {action}
      </div>
    </section>
  );
}

/** One person: identity, agreed week, employment record, access, and the action the task asks for. */
export function PersonRow({
  u,
  task,
  mayEdit,
  accessKnown,
  tenantId,
  companyWeek,
  tenantWeek,
  onOpen,
  onReview,
}: {
  u: Row;
  task: Task;
  mayEdit: boolean;
  accessKnown: boolean;
  tenantId: number | undefined;
  companyWeek: number;
  tenantWeek: number;
  onOpen: (u: Row, field?: string | null) => void;
  onReview: (id: number) => void;
}) {
  const name = fullName(u);
  const teamNames = teamsHere(u, tenantId).map((t) => t.name);
  const hours = hoursOf(u);
  const gaps = setupGaps(u) ?? [];
  const access = accessOf(u, accessKnown);
  const invite = invitation(u);
  const end = endNotice(u, new Date().toISOString().slice(0, 10));
  const hoursIssue = hours === 'missing' || hours === 'invalid';
  // the action the chosen task asks for; in the whole list the agreed week first (the prototype's order)
  let action: { label: string; warn?: boolean; go: () => void };
  if (!mayEdit) action = { label: T('Se profil'), go: () => onOpen(u) };
  else if (task === 'hours' || (task === 'all' && hoursIssue))
    action = { label: T('Sett arbeidstid'), warn: true, go: () => onOpen(u, 'agreed_week_hours') };
  else if (task === 'setup')
    action = { label: T('Fullfør opplysninger'), go: () => onOpen(u, fieldFor(u, 'setup')) };
  else if (task === 'number')
    action = { label: T('Fullfør ansattnummer'), go: () => onOpen(u, 'employee_id') };
  else if ((task === 'uninvited' || task === 'pending') && u.active_status && access && access !== 'in')
    action = {
      label: access === 'pending' ? T('Se og påminn') : T('Se og inviter'),
      go: () => onReview(u.id),
    };
  else action = { label: T('Åpne profil'), go: () => onOpen(u) };
  const accessIcon: IconName =
    access == null ? 'info' : access === 'uninvited' ? 'mail' : access === 'pending' ? 'clock' : 'check';
  const pct = stillingsprosent(u, tenantWeek);
  const form = u.employment_form
    ? T({ fast: 'Fast', midlertidig: 'Midlertidig', tilkalling: 'Tilkalling' }[u.employment_form])
    : '';
  return (
    <tr data-testid="person-row" data-id={u.id}>
      <td className="ans-who">
        <span className={`av ${avTone(u.id)}`} aria-hidden="true">
          {initials(name)}
        </span>
        <span>
          <a
            id={`ans-${u.id}`}
            href={`/ansatte/${u.id}`}
            className="ans-name"
            onClick={(e) => {
              e.preventDefault();
              onOpen(u);
            }}
          >
            {name}
          </a>
          <small>
            {teamNames.join(', ') || T('Uten team')}
            {mayEdit &&
              (u.employee_id ? `, ${T('Nr. {n}', { n: u.employee_id })}` : `, ${T('Nummer mangler')}`)}
            {!u.active_status && `, ${T('Inaktiv')}`}
          </small>
        </span>
      </td>
      {mayEdit && (
        <td className="ans-hours">
          <span className="ans-lbl">{T('Avtalt per uke')}</span>
          {/* #154 BR-11: the weekly hours as a tag beside the employment type; missing and invalid stay
              warnings, never an invented 0 or default */}
          {hours === 'missing' ? (
            <span className="ans-wk">
              <span className="ans-warn">{T('Ikke satt')}</span>
              {form && <span className="ans-form">{form}</span>}
              <small>{T('Reglene bruker {h} t', { h: num(companyWeek) })}</small>
            </span>
          ) : hours === 'invalid' ? (
            <span className="ans-wk">
              <span className="ans-warn">{T('Ugyldig: {n} t', { n: num(u.max_hours_per_week ?? 0) })}</span>
              {form && <span className="ans-form">{form}</span>}
              <small>{T('Reglene bruker {h} t', { h: num(companyWeek) })}</small>
            </span>
          ) : hours === 'unknown' ? (
            <span className="ans-mute">–</span>
          ) : (
            <span className="ans-wk">
              <span className="ans-fig">
                {num(u.max_hours_per_week ?? 0)} t{pct != null && `, ${pct} %`}
              </span>
              {form && <span className="ans-form">{form}</span>}
              {hours === 'company' && <small>{T('Bedriftens uke')}</small>}
            </span>
          )}
        </td>
      )}
      {mayEdit && (
        <td className="ans-data">
          <span className="ans-lbl">{T('Ansettelsesdata')}</span>
          {gaps.some((g) => g !== 'number_format') && (
            <span className="ans-warn">{gapPhrase(gaps.filter((g) => g !== 'number_format'))}</span>
          )}
          {u.number_problem && (
            // the server's reason names the field and the number: «Lønns-ID «1234567» passer ikke i PowerOffice …»
            <span
              className="ans-warn ans-why"
              data-testid="number-problem"
              data-field={u.number_problem.field}
            >
              {u.number_problem.reason}
            </span>
          )}
          {!gaps.length && <span className="ans-mute">{T('Nummer og datoer registrert')}</span>}
          {end && (
            <small className={end.kind === 'passed' ? 'ans-dgr' : 'ans-warn'}>
              {end.kind === 'passed'
                ? T('Sluttet {d}', { d: fmtDs(end.date) })
                : T('Slutter {d}', { d: fmtDs(end.date) })}
            </small>
          )}
        </td>
      )}
      {!mayEdit && (
        <td className="ans-data">
          <span className="ans-lbl">{T('Kontakt')}</span>
          {/* an address breaks after the @, not in the middle of a word */}
          <span>
            {u.phone ||
              (u.email ? (
                <>
                  {u.email.split('@')[0]}@<wbr />
                  {u.email.split('@').slice(1).join('@')}
                </>
              ) : (
                '–'
              ))}
          </span>
        </td>
      )}
      <td className="ans-acc">
        <span className="ans-lbl">{T('Tilgang')}</span>
        <span className={`ans-access ${access ?? 'unknown'}`}>
          <Icon name={accessIcon} size={15} strokeWidth={1.7} />
          <span>
            {access == null
              ? T('Ikke tilgjengelig')
              : access === 'uninvited'
                ? T('Ikke invitert')
                : access === 'pending'
                  ? T('Invitasjon sendt')
                  : T('Har logget inn')}
          </span>
        </span>
        {/* how the newest invitation left (ONB-11): mailed, shown, failed or expired, with its day */}
        {access && access !== 'in' && invite.kind !== 'none' && (
          <small>{inviteText(invite.kind, invite.date)}</small>
        )}
        {access === 'in' && u.has_app && <small>{T('Bruker appen')}</small>}
        {access === 'uninvited' && !u.email && <small>{T('Mangler e-post')}</small>}
        {accessKnown && u.email && likelyDomain(u.email) && (
          <Link to={`/ansatte/${u.id}?rediger=email`} className="ans-typo" data-testid="ansatte-epost-sjekk">
            {T('Mente du {d}?', { d: likelyDomain(u.email) ?? '' })}
          </Link>
        )}
      </td>
      <td className="ans-act">
        <button
          type="button"
          className={`mlink${action.warn ? ' warn' : ''}`}
          onClick={action.go}
          aria-label={`${action.label}, ${name}`}
        >
          {action.label} <Icon name="chevronRight" />
        </button>
      </td>
    </tr>
  );
}
